import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { trackEvent } from "@/lib/analytics/events";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, UserPlus, Loader2 } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { toLoginEmail, useAuth } from "@/lib/auth/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Where a successful sign-in lands when the CTA did not say otherwise. */
const APP_HOME = "/app/analyze";

/** Only same-site paths are accepted, and never back to /auth (loop guard). */
function safeRedirect(value: unknown): string {
  const raw = typeof value === "string" ? value : "";
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/auth")) return APP_HOME;
  return raw;
}

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const r = typeof search["redirect"] === "string" ? (search["redirect"] as string) : undefined;
    return r ? { redirect: r } : {};
  },
  head: () => ({
    meta: [
      { title: "Sign In — AROS FLOW Underwriting Account" },
      {
        name: "description",
        content:
          "Sign in to AROS FLOW with your username and password to sync live seller-call underwriting sessions across every device you use.",
      },
      { property: "og:title", content: "Sign In — AROS FLOW Underwriting Account" },
      {
        property: "og:description",
        content:
          "Use your username and password to resume any AROS FLOW call session on any device.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AuthPage,
  errorComponent: ({ error }) => (
    <div className="flex min-h-dvh items-center justify-center px-4 text-center">
      <p className="text-sm text-muted-foreground">Sign-in didn't load: {error.message}</p>
    </div>
  ),
});

function AuthPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const target = safeRedirect(search.redirect);
  const { user, ready } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  /** Inline error so a failure stays visible even if the toast is missed. */
  const [err, setErr] = useState<string | null>(null);
  /** Set when the user arrives from a password-reset email. */
  const [recovering, setRecovering] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined" && /type=recovery/.test(window.location.hash)) {
      setRecovering(true);
    }
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setRecovering(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (ready && user && !recovering)
      void navigate({ to: target as "/app/analyze", replace: true });
  }, [ready, user, navigate, recovering, target]);

  /** Reset email can only go somewhere real, so it needs an email address. */
  const sendReset = async () => {
    const id = username.trim();
    setErr(null);
    if (!id.includes("@")) {
      const m =
        "Enter the email address on your account above, then tap this again. Logins created with a username only have no email on file — create a new login or contact support.";
      setErr(m);
      toast.error("Enter your account email address first");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(id, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) throw error;
      toast.success("Reset link sent — check your email");
    } catch (error) {
      const m = error instanceof Error ? error.message : "Could not send the reset email";
      setErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  const saveNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setErr(null);
    if (newPassword.length < 8) {
      setErr("Choose a password of at least 8 characters that is not a common one");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success("Password updated");
      setRecovering(false);
      setNewPassword("");
      void navigate({ to: target as "/app/analyze", replace: true });
    } catch (error) {
      const m = error instanceof Error ? error.message : "Could not update the password";
      setErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return; // double-submit guard
    setErr(null);
    if (!username.trim() || (mode === "signup" && password.length < 8) || (mode === "signin" && !password)) {
      const m =
        mode === "signup"
          ? "Enter a username and a password of at least 8 characters"
          : "Enter your username and password";
      setErr(m);
      toast.error(m);
      return;
    }
    setBusy(true);
    if (mode === "signup") void trackEvent("signup_started");
    const id = username.trim();
    const email = id.includes("@") ? id : toLoginEmail(id);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}${target}`,
            data: { display_name: username.trim() },
          },
        });
        if (error) throw error;
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        void trackEvent("signup");
        void trackEvent("signup_completed");
        toast.success("Account created — your deals now sync to this login");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        void trackEvent("login_completed");
        toast.success("Signed in — restoring your synced deals");
      }
      void navigate({ to: target as "/app/analyze", replace: true });
    } catch (error) {
      const raw = error instanceof Error ? error.message : "Sign-in failed";
      const message = /invalid login/i.test(raw)
        ? "That username and password don't match — or tap Create a new login"
        : /already registered|already exists|user_already/i.test(raw)
          ? "That username already exists — switch to Sign in"
          : /rate limit|too many/i.test(raw)
            ? "Too many attempts — wait a few seconds and try again"
            : /fetch|network/i.test(raw)
              ? "Network hiccup — check your connection and try again"
              : raw;
      if (/already registered|already exists|user_already/i.test(raw)) setMode("signin");
      setErr(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-black/40 p-6 shadow-xl">
        <h1 className="text-2xl font-semibold tracking-tight text-gold">AROS FLOW</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {mode === "signin"
            ? "Enter your username and password to restore your live call sessions on this device."
            : "Pick a username and password. Every deal you work is saved to that login."}
        </p>

        {recovering ? (
          <form onSubmit={saveNewPassword} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="auth-new-password">New password</Label>
              <Input
                id="auth-new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters, not a common one"
              />
            </div>
            {err ? (
              <p
                role="alert"
                className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
              >
                {err}
              </p>
            ) : null}
            <Button
              type="submit"
              disabled={busy}
              className="focus-key w-full bg-[var(--gold)] font-semibold text-black hover:bg-[var(--gold)]/90"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
              Save new password
            </Button>
          </form>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="auth-username">Username or email</Label>
              <Input
                id="auth-username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="yourname or you@email.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="auth-password">Password</Label>
              <Input
                id="auth-password"
                type="password"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters, not a common one"
              />
            </div>
            {err ? (
              <p
                role="alert"
                className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
              >
                {err}
              </p>
            ) : null}
            <Button
              type="submit"
              disabled={busy}
              className="focus-key w-full bg-[var(--gold)] font-semibold text-black hover:bg-[var(--gold)]/90"
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : mode === "signin" ? (
                <LogIn className="mr-2 h-4 w-4" aria-hidden />
              ) : (
                <UserPlus className="mr-2 h-4 w-4" aria-hidden />
              )}
              {mode === "signin" ? "Sign in" : "Create account"}
            </Button>
          </form>
        )}

        <div className="mt-4 flex items-center justify-between text-xs">
          <button
            type="button"
            className="focus-key text-muted-foreground underline hover:text-foreground"
            onClick={() => {
              setErr(null);
              setMode(mode === "signin" ? "signup" : "signin");
            }}
          >
            {mode === "signin" ? "Create a new login" : "I already have a login"}
          </button>
          <button
            type="button"
            onClick={sendReset}
            disabled={busy}
            className="focus-key text-muted-foreground underline hover:text-foreground"
          >
            Forgot password?
          </button>
        </div>

        <div className="mt-2 text-right text-xs">
          <Link to="/" className="focus-key text-muted-foreground underline hover:text-foreground">
            Back to app
          </Link>
        </div>
      </div>
    </main>
  );
}
